import { useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { getPortfolio } from 'services/RewardsService'
import { PortfolioStatus } from 'types/rewards'

const POLLING_INTERVAL = 1000
const MAX_POLL_COUNT = 5

interface UsePortfolioQueryOptions {
  portfolioId: string
  enabled?: boolean
  pollUntilActive?: boolean
  poolMaxCount?: number
  onMaxPollReached?: () => void
  onPortfolioCreatedFailed?: () => void
}

export const usePortfolioQuery = ({
  portfolioId,
  enabled = true,
  pollUntilActive = false,
  poolMaxCount = MAX_POLL_COUNT,
  onMaxPollReached,
  onPortfolioCreatedFailed,
}: UsePortfolioQueryOptions) => {
  const pollCountRef = useRef(0)

  return useQuery({
    queryKey: reactQueryKeys.rewardsPortfolio(portfolioId),
    queryFn: () => getPortfolio(portfolioId),
    enabled,
    refetchInterval: query => {
      if (!pollUntilActive) return false

      // Stop polling if query has error (Bad Request, Network error, etc.)
      if (query.state.error) {
        pollCountRef.current = 0
        onPortfolioCreatedFailed?.()
        return false
      }

      const portfolio = query.state.data
      if (!portfolio) return POLLING_INTERVAL

      // Portfolio is active - stop polling successfully
      if (portfolio.status === PortfolioStatus.ACTIVE) {
        pollCountRef.current = 0
        return false
      }

      // Portfolio is closed - creation failed, stop polling with error
      if (portfolio.status === PortfolioStatus.CLOSED) {
        pollCountRef.current = 0
        onPortfolioCreatedFailed?.()
        return false
      }

      pollCountRef.current++

      if (pollCountRef.current >= poolMaxCount) {
        pollCountRef.current = 0
        onMaxPollReached?.()
        return false
      }

      return POLLING_INTERVAL
    },
    refetchIntervalInBackground: false,
  })
}
